import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import moment from 'moment';
import { TextField } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import ManageProgramContext from '../../../../../../context/ManageProgramContext';
import User from '../../../../../../../Model/User';
import DialogRoundedModal from '../../../../../../../components/DialogRoundedModal';
import { SaveButton, CancelButton } from '../../../../../../../components/Button/ActionButtons';
import LoadingOverlay from '../../../../../LoadingOverlay';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../../../hooks/useLogger';
import useToast from '../../../../../../hooks/useToast';
import useScheduleTimeLabel from '../../../../../../hooks/useScheduleTimeLabel';
import { ScheduledAssignmentType } from '../../../../../../../Model/ScheduledAssignment';
import { CoachingActivity } from '../../../../../../../utils/log';
import { DateFormat } from '../../../../../../../utils/date';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import AssignmentRepeatInput from '../AssignmentRepeatInput';

import { InfoTag, WarningTag, AttentionTag } from '../../../../../../../components/Tags';
import { DifficultyLevel } from '../../../../../../../Model/Program';
import QAPContext from '../../../../../../../context/QAPContext';
import AssignmentProductSelector from '../../../../../AssignmentProductSelector';
import {
  DialogBody,
  StyledAutocomplete,
  StyledOption,
  ColumnContainer,
  RowContainer,
  StyledHelperText,
  CheckBoxContainer,
  StyledCheckBox,
} from './styles';
import texts from './texts.json';

const DEFAULT_REPETITIONS = 1;

const DIFFICULTY_TAG_MAP = {
  [DifficultyLevel.BEGINNER]: InfoTag,
  [DifficultyLevel.INTERMEDIATE]: WarningTag,
  [DifficultyLevel.ADVANCED]: AttentionTag,
};

const ProgramAssignmentModal = ({
  showModal,
  onClose,
  userDoc,
  selectedDate,
}) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [isKeepWindowOpen, setIsKeepWindowOpen] = useState(false);
  const [programRepetitions, setProgramRepetitions] = useState(DEFAULT_REPETITIONS);
  const [isScheduled, setIsScheduled] = useState(true);
  // Seeded from the quick access panel, but only when it was opened for this client.
  const { productIdForUser } = useContext(QAPContext);
  const [selectedProductId, setSelectedProductId] = useState(productIdForUser(userDoc?.id));

  const { externalCoachDoc: { id: coachId } } = useContext(ExternalCoachContext);
  const { coachPrograms, isReady } = useContext(ManageProgramContext);

  const { firebase } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const { scheduleTimeLabel } = useScheduleTimeLabel();

  const programOptions = useMemo(() => coachPrograms.map((program) => ({
    id: program.id,
    label: program.name,
    difficulty: program.difficulty,
    program,
  })), [coachPrograms]);

  const endDate = useMemo(() => {
    const days = selectedProgram?.program.workouts.length * programRepetitions;
    return moment(selectedDate).add(days, 'day').format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT);
  }, [
    selectedDate,
    programRepetitions,
    selectedProgram,
  ]);

  const onAssignProgram = useCallback(async () => {
    setShowLoading(true);
    try {
      const response = await firebase.remote('workout-scheduleAssignmentEndpoint', {
        type: ScheduledAssignmentType.PROGRAM,
        userId: userDoc.id,
        coachId,
        displayName: selectedProgram.label,
        isScheduled,
        payload: {
          programId: selectedProgram.program.id,
          repetitions: programRepetitions,
          startDate: selectedDate,
        },
        ...(selectedProductId && { productId: selectedProductId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error assigning program');
      }

      if (isComponentMountedRef.current) {
        showToast(isScheduled ? texts.programScheduled : texts.programAssigned);
      }

      logCoachingActivity(CoachingActivity.ASSIGNED_PROGRAM, {
        programId: selectedProgram.program.id,
        clientId: userDoc.id,
        ...(selectedProductId && { productId: selectedProductId }),
      });

      if (isComponentMountedRef.current) {
        setShowLoading(false);
        setSelectedProgram(null);
        if (!isKeepWindowOpen) {
          onClose();
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(texts.errorAssigningProgram, { error: true });
        setShowLoading(false);
      }
    }
  }, [
    coachId,
    firebase,
    isComponentMountedRef,
    isScheduled,
    selectedProductId,
    userDoc,
    isKeepWindowOpen,
    onClose,
    programRepetitions,
    selectedDate,
    selectedProgram,
    showToast,
    logCoachingActivity,
  ]);

  const isMissingProduct = !!userDoc?.hasMultipleActiveProducts && !selectedProductId;

  return (
    <DialogRoundedModal
      title={texts.title}
      description={selectedDate}
      fullWidth
      maxWidth="sm"
      actionButtons={(
        <ColumnContainer>
          <CheckBoxContainer>
            <StyledCheckBox
              checked={isScheduled}
              onChange={() => setIsScheduled((prev) => !prev)}
              size="small"
            />
            <StyledHelperText>{scheduleTimeLabel}</StyledHelperText>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <StyledCheckBox
              checked={isKeepWindowOpen}
              onChange={() => setIsKeepWindowOpen((prev) => !prev)}
              size="small"
            />
            <StyledHelperText>{texts.keepWindowOpen}</StyledHelperText>
          </CheckBoxContainer>
          <RowContainer>
            <SaveButton
              disabled={!selectedProgram || !(programRepetitions > 0) || isMissingProduct}
              icon={<CheckCircleOutline />}
              onClick={onAssignProgram}
            >
              {texts.assignProgram}
            </SaveButton>
            <CancelButton onClick={onClose}>{texts.cancel}</CancelButton>
          </RowContainer>
        </ColumnContainer>
      )}
      open={showModal}
      onClose={onClose}
    >
      <AssignmentProductSelector
        userDoc={userDoc}
        selectedProductId={selectedProductId}
        onSelect={setSelectedProductId}
      />
      <DialogBody>
        <StyledAutocomplete
          disablePortal
          value={selectedProgram}
          options={programOptions}
          renderInput={(params) => <TextField {...params} label={texts.selectProgram} />}
          onChange={(event, value) => setSelectedProgram(value)}
          renderOption={(props, {
            id,
            label,
            difficulty,
          }) => {
            const DifficultyTag = DIFFICULTY_TAG_MAP[difficulty];
            return (
              <StyledOption {...props} key={id} className="option-list">
                {label}
                {!!DifficultyTag && <DifficultyTag>{difficulty}</DifficultyTag>}
              </StyledOption>
            );
          }}
        />
        <AssignmentRepeatInput
          helperText={texts.repeatHelperText}
          value={programRepetitions}
          onChange={setProgramRepetitions}
          startDate={selectedDate}
          endDate={endDate}
        />
      </DialogBody>
      <LoadingOverlay isLoading={showLoading || !isReady} />
    </DialogRoundedModal>
  );
};

ProgramAssignmentModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userDoc: PropTypes.instanceOf(User),
  selectedDate: PropTypes.string.isRequired,
};

ProgramAssignmentModal.defaultProps = {
  userDoc: null,
};

export default compose(
  observer,
)(ProgramAssignmentModal);
